import { useFormAugmentedContext } from '@framework/form/form/augmented/FormAugmentedContext.tsx';
import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import type * as React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TbChevronDown, TbX } from 'react-icons/tb';
import styled from 'styled-components';
import { IconWrapper } from '../../../icon/IconWrapper.tsx';
import { type IPopoverHandle, Popover } from '../../../popover/Popover.tsx';
import { getSearchableFieldNames } from '../../getSearchableFieldNames.ts';
import {
  isRangeSearchableField,
  type SearchableField,
} from '../../ISearchableField.tsx';
import { useApplyFilterValues } from '../hooks/useApplyFilterValues.ts';
import { FilterBarEditPopoverContent } from '../popover/FilterBarEditPopoverContent.tsx';
import { formatFilterBarValue } from '../utils/formatFilterBarValue.tsx';
import { removeSearchableField } from '../utils/removeSearchableField.ts';

const barChipPopoverOpenClassName = 'bar-chip-popover-open';

// Allineato all'altezza di <Button size="small"> di Ant (32px).
const BarChipWrapper = styled.span`
  --bar-chips-background-color: ${props => props.theme.colorBgFilterBarChip};
  --bar-chips-border-color: ${props => props.theme.colorBorderFilterBarChip};

  display: inline-flex;
  align-items: stretch;
  height: 32px;
  border: 1px solid var(--bar-chips-border-color);
  border-radius: 4px;
  font-size: 13px;
  line-height: 1;
  overflow: hidden;
  user-select: none;
  background-color: var(--bar-chips-background-color);
  color: ${props => props.theme.colorTextSecondary};


  &:focus-within,
  &:has(.${barChipPopoverOpenClassName}) {
    border-color: ${props => props.theme.colorPrimary};
    box-shadow: 0 0 0 ${props => props.theme.controlOutlineWidth}px
      ${props => props.theme.controlOutline};
  }
`;

const BarChipMainButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: none;
  padding: 0 6px 0 10px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  background-color: var(--bar-chips-background-color);

  &:hover {
    background: ${props => props.theme.colorBgTextHover};
  }

  &:focus-visible {
    outline: ${props => props.theme.lineWidthFocus}px solid
      ${props => props.theme.focusOutlineColor};
    outline-offset: -2px;
  }
`;

const BarChipValue = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colorText};
`;

const BarChipDivider = styled.span`
  width: 1px;
  background: var(--bar-chips-border-color);
`;

const BarChipCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0 6px;
  cursor: pointer;
  color: ${props => props.theme.colorTextTertiary};

  &:hover {
    background: ${props => props.theme.colorBgTextHover};
    color: ${props => props.theme.colorText};
  }

  &:focus-visible {
    outline: ${props => props.theme.lineWidthFocus}px solid
      ${props => props.theme.focusOutlineColor};
    outline-offset: -2px;
  }
`;

export interface IFilterBarChipProps {
  field: SearchableField;
  /** Necessario per il dispatcher `AdvancedSearchField` nel popover edit. */
  fields: SearchableField[];
  /**
   * Query corrente (parsata): sorgente di verità per inizializzare il
   * `FormHandler` annidato del popover edit. Evita di leggere lo stato del
   * form esterno (che potrebbe contenere valori "fantasma" per i campi con
   * vuoto ambiguo come multi-select e radio).
   */
  query: Record<string, any>;
  /** Schema dei filtri, propagato al `FormHandler` annidato del popover. */
  schema: YupAnyObjectSchema;
  /** Callback per rimuovere il chip dall'elenco shown. Eseguita prima di unregister + submit. */
  onRemove: () => void;
}

/**
 * Singolo chip di filtro: `label : valore` (label-only se senza valore).
 * Click sul corpo apre il popover di edit; "×" rimuove il chip (unregister +
 * submit, replica `AdvancedSearchFormSection.onRemove`).
 */
export function FilterBarChip({
  field,
  fields,
  query,
  schema,
  onRemove,
}: Readonly<IFilterBarChipProps>) {
  const { t } = useTranslation();
  const methods = useFormContext();
  const augmented = useFormAugmentedContext();
  const popoverRef = useRef<IPopoverHandle>(null);

  const fieldNames = getSearchableFieldNames(field);
  const watched = useWatch({
    name: fieldNames,
    control: methods.control,
  }) as unknown[];
  const fromValue = watched?.[0];
  const toValue = watched?.[1];

  const valueForFormat = useMemo(
    () =>
      isRangeSearchableField(field)
        ? { from: fromValue, to: toValue }
        : fromValue,
    [field, fromValue, toValue],
  );

  const formatted = formatFilterBarValue(field, valueForFormat, t);

  // Rimozione del chip: prima lo toglie dalla lista mostrata (`onRemove`),
  // poi fa unregister del campo + submit esterno (`removeSearchableField`).
  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove();
      removeSearchableField({ field, methods, augmented });
    },
    [onRemove, field, methods, augmented],
  );

  const applyValues = useApplyFilterValues(field);

  // L'editor chiama un'unica callback: applichiamo i valori al form esterno e
  // chiudiamo il popover.
  const handleApplied = useCallback(
    (values: Record<string, unknown>) => {
      applyValues(values);
      popoverRef.current?.close();
    },
    [applyValues],
  );

  const editContent = (
    <FilterBarEditPopoverContent
      field={field}
      fields={fields}
      query={query}
      schema={schema}
      onApplied={handleApplied}
      onCancel={() => popoverRef.current?.close()}
    />
  );

  return (
    <BarChipWrapper data-searchid={field.name}>
      <Popover
        arrow={false}
        ref={popoverRef}
        content={editContent}
        placement="bottomLeft"
        openClassName={barChipPopoverOpenClassName}
        autoFocusField
        allowPortalClicks
      >
        <BarChipMainButton type="button" aria-haspopup="dialog">
          <span>{field.label}</span>
          {/* Check esplicito null/undefined: un `renderValue` di un Custom
              field può ritornare `0` o `false`, che la truthiness scarterebbe. */}
          {formatted != null && (
            <>
              <span>:</span>
              <BarChipValue>{formatted}</BarChipValue>
            </>
          )}
          <IconWrapper icon={<TbChevronDown />} />
        </BarChipMainButton>
      </Popover>
      <BarChipDivider />
      <BarChipCloseButton
        type="button"
        onClick={handleRemove}
        aria-label={t(
          'filter-bar.remove-filter-aria',
          'Rimuovi filtro {{label}}',
          { label: field.label },
        )}
      >
        <IconWrapper icon={<TbX />} />
      </BarChipCloseButton>
    </BarChipWrapper>
  );
}
